import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { FeedFilterBar, type FeedFilterValues } from "@/features/feed/components/feed-filter-bar";
import { FeedFilters } from "@/features/feed/components/feed-filters";
import { NewPostDialog } from "@/features/feed/components/new-post-dialog";
import { PostCard, PostCardSkeleton } from "@/features/feed/components/post-card";
import { toRoleName } from "@/features/dashboard/utils/layout";
import { usePosts } from "@/features/feed/hooks/use-posts";
import type { FeedFilter } from "@/features/feed/types";
import { formatPostDate, groupPostsByDate } from "@/features/feed/utils/format";
import { useMe } from "@/hooks/use-me.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";

export function FeedPage() {
	const [filter, setFilter] = useState<FeedFilter>("all");
	const [searchParams, setSearchParams] = useSearchParams();
	const me = useMe();
	const { has } = usePermissions();
	const isCliente = toRoleName(me.data?.account?.role?.name) === "cliente";

	const filters: FeedFilterValues = useMemo(
		() => ({
			stageId: searchParams.get("etapa") ?? undefined,
			occurredFrom: searchParams.get("de") ?? undefined,
			occurredTo: searchParams.get("ate") ?? undefined,
			authorUserId: searchParams.get("autor") ?? undefined,
		}),
		[searchParams],
	);

	function setFilters(next: FeedFilterValues) {
		const params = new URLSearchParams();
		if (next.stageId) params.set("etapa", next.stageId);
		if (next.occurredFrom) params.set("de", next.occurredFrom);
		if (next.occurredTo) params.set("ate", next.occurredTo);
		if (next.authorUserId) params.set("autor", next.authorUserId);
		setSearchParams(params, { replace: true });
	}

	const { data: posts, isLoading, isError } = usePosts({
		visibility: filter,
		...filters,
	});

	const groups = useMemo(() => groupPostsByDate(posts ?? []), [posts]);

	return (
		<div className="feed-page">
			<div className="page-header">
				<div>
					<h1 className="page-title">Diário de Obra</h1>
					<p className="page-subtitle">
						Registro cronológico — visão completa, incluindo relatos internos.
					</p>
				</div>
				<div className="page-header__actions">
					{!isCliente && <FeedFilters value={filter} onChange={setFilter} />}
					{has("posts:create") && <NewPostDialog />}
				</div>
			</div>

			<FeedFilterBar value={filters} onChange={setFilters} />

			{isLoading ? (
				<div className="feed-skeleton">
					<PostCardSkeleton />
					<PostCardSkeleton />
					<PostCardSkeleton />
				</div>
			) : isError ? (
				<p className="feed-page__error">
					Não foi possível carregar o feed. Tente novamente.
				</p>
			) : groups.length > 0 ? (
				<div className="feed-page__groups">
					{groups.map(([day, dayPosts]) => (
						<div key={day}>
							<div className="feed-day">
								<span className="feed-day__label">
									{formatPostDate(`${day}T00:00`)}
								</span>
								<div className="feed-day__rule" />
								<span className="feed-day__count">
									{dayPosts.length}{" "}
									{dayPosts.length === 1 ? "relato" : "relatos"}
								</span>
							</div>
							<div className="feed-day__posts">
								{dayPosts.map((post) => (
									<PostCard
										key={post.id}
										post={post}
										onStageSelect={(stageId) =>
											setFilters({ ...filters, stageId })
										}
									/>
								))}
							</div>
						</div>
					))}
				</div>
			) : (
				<p className="feed-page__hint">Nenhum relato para este filtro.</p>
			)}
		</div>
	);
}
